import { useEffect, useState } from "react";
import { toast } from "sonner";

interface ShareBarProps {
  url: string;
  title: string;
  text?: string;
}

export function ShareBar({ url, title, text }: ShareBarProps) {
  const [busy, setBusy] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);

  // Gate to prevent SSR/client hydration mismatch — navigator.share is
  // only available in the browser, so check after mount.
  useEffect(() => {
    setCanNativeShare(
      typeof navigator !== "undefined" && typeof (navigator as any).share === "function",
    );
  }, []);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedText = encodeURIComponent(text ?? title);

  const links = [
    { name: "X", href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}` },
    { name: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}` },
    { name: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` },
    { name: "Reddit", href: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}` },
    { name: "Email", href: `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}` },
  ];

  async function nativeShare() {
    if (busy) return;
    if (typeof navigator !== "undefined" && typeof (navigator as any).share === "function") {
      setBusy(true);
      try {
        await (navigator as any).share({ title, text, url });
      } catch {
        // user cancelled
      } finally {
        setBusy(false);
      }
    } else {
      copyLink();
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy link");
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="eyebrow mr-1">Share</span>
      {links.map((l) => (
        <a
          key={l.name}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-3 py-2 min-h-11 border border-border hover:border-foreground hover:text-foreground text-muted-foreground transition-colors"
        >
          {l.name}
        </a>
      ))}
      <button
        type="button"
        onClick={copyLink}
        className="inline-flex items-center px-3 py-2 min-h-11 border border-border hover:border-foreground hover:text-foreground text-muted-foreground transition-colors"
      >
        Copy link
      </button>
      {canNativeShare && (
        <button
          type="button"
          onClick={nativeShare}
          disabled={busy}
          className="inline-flex items-center px-3 py-2 min-h-11 border border-border hover:border-foreground hover:text-foreground text-muted-foreground transition-colors sm:hidden"
        >
          Share…
        </button>
      )}
    </div>
  );
}

